import { useCallback, useState } from 'react';
import type { RoutineDifficulty } from '../types/routine';
import { ROUTINE_DIFFICULTIES } from '../types/routine';

const STORAGE_KEY = 'gym.routineDifficulty';

/**
 * El nivel elegido no vive en el perfil del usuario: `user_profiles` no tiene
 * columna para ello y añadirla obligaría a migrar y a decidir qué hacer con
 * los 7 usuarios existentes. Guardarlo en localStorage cubre el caso real
 * (cada quien entrena desde su móvil) sin tocar el esquema.
 *
 * Si algún día hace falta que el nivel siga al usuario entre dispositivos,
 * esto se sustituye por una llamada al perfil sin cambiar el resto.
 */
const isValid = (value: string | null): value is RoutineDifficulty =>
  value !== null && (ROUTINE_DIFFICULTIES as readonly string[]).includes(value);

function readStored(): RoutineDifficulty {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValid(stored) ? stored : 'intermediate';
  } catch {
    // localStorage puede fallar en modo privado de algunos navegadores.
    return 'intermediate';
  }
}

export function useRoutineDifficulty() {
  const [difficulty, setDifficultyState] =
    useState<RoutineDifficulty>(readStored);

  const setDifficulty = useCallback((next: RoutineDifficulty) => {
    setDifficultyState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Si no se puede persistir, al menos la sesión actual respeta la elección.
    }
  }, []);

  return { difficulty, setDifficulty };
}
